// ABOUTME: Renders a tree-based lookup data scope field for role editing.
// ABOUTME: It loads hierarchy data and keeps tree selection synchronized with form values.

import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Controller, useFormContext} from 'react-hook-form'

import {CheckboxTreeNode} from '../../../common/types/tree'
import {markSelectedNodesInTree} from '../../../common/utils/utils'
import {
  CHANGE_COMMON_DATASCOPE_VALUE,
  GroupedRoleReducerAction
} from '../../../modules/GroupedRoleAssignmentReducer'
import {TreeLookupDataScopeInput} from '../../../modules/DataScopeConfiguration'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'

import {TreeLookupField} from './TreeLookupField'

type TreeLookupDataScopeProps = {
  dataScopeName: string
  errorMessage: string
  configuration: TreeLookupDataScopeInput
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
}

export const TreeLookupDataScope = ({
  dataScopeName,
  errorMessage,
  configuration,
  dispatchRoleStateUpdate
}: TreeLookupDataScopeProps) => {
  const {control, getValues, watch} = useFormContext<GroupedRoleAssignment>()
  const [searchValue, setSearchValue] = useState('')
  const [treeData, setTreeData] = useState<CheckboxTreeNode[]>([])

  const formValues = watch()
  const dependencies = configuration.dependsOn.map((dep) => formValues.commonDataScopes[dep])

  const {isError, isLoading, data} = useQuery(
    [dataScopeName, ...dependencies],
    () => configuration.fetchNodes(dependencies),
    {retry: false}
  )

  useEffect(() => {
    if (!data) {
      return
    }

    const existingValues = getValues().commonDataScopes[dataScopeName]
    const selectedNodeIds = Array.isArray(existingValues) ? existingValues : []

    if (selectedNodeIds.length > 0) {
      setTreeData(markSelectedNodesInTree(data, selectedNodeIds))
      return
    }

    setTreeData(data)
  }, [data, dataScopeName, getValues])

  return (
    <Controller
      name={`commonDataScopes.${dataScopeName}` as keyof GroupedRoleAssignment}
      control={control}
      render={({field}) => {
        const handleOnSelectedNodeIdsChange = (selectedNodeIds: (string | number)[]) => {
          field.onChange(selectedNodeIds)

          dispatchRoleStateUpdate({
            type: CHANGE_COMMON_DATASCOPE_VALUE,
            payload: {
              name: dataScopeName,
              value: selectedNodeIds.map((id) => id.toString())
            }
          })
        }

        return (
          <TreeLookupField
            field={field}
            onSelectedNodeIdsChange={handleOnSelectedNodeIdsChange}
            dataScopeName={dataScopeName}
            errorMessage={errorMessage}
            treeData={treeData}
            setTreeData={setTreeData}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isLoading={isLoading}
            isError={isError}
          />
        )
      }}
    />
  )
}
